import React, { useCallback, useState } from 'react';

import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { getIsPausedOrCancelledCategory } from 'src/components/workflow/phase-column-helpers';
import { Apollo } from 'src/gql/generated';
import { useStartWorkflowWork } from 'src/hooks/workflow/workflow';

export interface ParkedWorkflowPrompt {
  columnName: string;
  isUnstarted: boolean;
}

export type ParkedWorkflowMeta = Pick<
  NonNullable<Apollo.GetWorkflowPhasesQuery['workflow']>,
  'id' | 'startedAt' | 'variant'
> & {
  column: Pick<
    NonNullable<Apollo.GetWorkflowPhasesQuery['workflow']>['column'],
    'category' | 'name'
  >;
};

interface UseParkedWorkflowCompletionArgs {
  onComplete: (options: { confirmUnpark: boolean }) => Promise<unknown> | void;
  workflow: ParkedWorkflowMeta;
  /**
   * False for an external: the board is the owner org's, so they complete without being asked and
   * the workflow stays where its owners left it. Starting one also needs edit access they lack.
   */
  canManageWorkflow?: boolean;
}

/**
 * Completing a task on a workflow that is going nowhere — unstarted, or parked in Paused or
 * Cancelled — leaves the card where it is: the phase that the completion starts cannot move a
 * workflow that never started, and parked columns hold their cards on purpose. Ask first, then
 * start or resume the workflow as part of completing the task.
 */
export function useParkedWorkflowCompletion({
  canManageWorkflow = true,
  onComplete,
  workflow,
}: UseParkedWorkflowCompletionArgs): {
  completeTask: () => void;
  confirmAndComplete: () => Promise<void>;
  dismissPrompt: () => void;
  prompt: ParkedWorkflowPrompt | null;
} {
  const { startWorkflowWork } = useStartWorkflowWork(workflow.id);
  const [prompt, setPrompt] = useState<ParkedWorkflowPrompt | null>(null);

  const completeTask = useCallback(() => {
    const parkedPrompt = canManageWorkflow ? getParkedWorkflowPrompt(workflow) : null;
    if (!parkedPrompt) {
      void onComplete({ confirmUnpark: false });
      return;
    }

    setPrompt(parkedPrompt);
  }, [canManageWorkflow, onComplete, workflow]);

  const confirmAndComplete = useCallback(async () => {
    if (!prompt) return;
    if (prompt.isUnstarted) await startWorkflowWork();

    await onComplete({ confirmUnpark: true });
  }, [onComplete, prompt, startWorkflowWork]);

  const dismissPrompt = useCallback(() => setPrompt(null), []);

  return { completeTask, confirmAndComplete, dismissPrompt, prompt };
}

interface ParkedWorkflowCompletionDialogProps {
  onClose: () => void;
  onConfirm: () => Promise<void>;
  prompt: ParkedWorkflowPrompt | null;
}

export function ParkedWorkflowCompletionDialog({
  onClose,
  onConfirm,
  prompt,
}: ParkedWorkflowCompletionDialogProps): JSX.Element | null {
  if (!prompt) return null;

  if (prompt.isUnstarted)
    return (
      <ConfirmationDialog
        open
        message={`This workflow is in "${prompt.columnName}" and has not started yet. Completing this task starts the workflow and its first phase.`}
        primaryText="Start and Complete"
        title="Start this workflow?"
        onClickPrimary={onConfirm}
        onClose={onClose}
      />
    );

  return (
    <ConfirmationDialog
      open
      message={`This workflow is parked in "${prompt.columnName}". Completing this task moves it back to the current phase's column.`}
      primaryText="Resume and Complete"
      title="Resume this workflow?"
      onClickPrimary={onConfirm}
      onClose={onClose}
    />
  );
}

function getParkedWorkflowPrompt(workflow: ParkedWorkflowMeta | null): ParkedWorkflowPrompt | null {
  // Template tasks are authoring artifacts: their workflow never starts and never moves.
  if (!workflow || workflow.variant === Apollo.WorkflowVariant.Template) return null;
  if (!workflow.startedAt) return { columnName: workflow.column.name, isUnstarted: true };
  if (!getIsPausedOrCancelledCategory(workflow.column.category)) return null;

  return { columnName: workflow.column.name, isUnstarted: false };
}
